// StrataTopBar — slim brand header above every page.
//   - Primary horizontal logo on the left + per-page title
//   - Search in the middle: smart search (every record type + recent activity) where the company has it on, else the
//     older jobs / leads / permits search; Ctrl+K or /
//   - Live date on the right
// Hidden on mobile and in print mode.

import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { pageTitleFromDocument } from './helpers';
import HeaderSearch from './HeaderSearch';
import SmartSearch from './smartSearch/SmartSearch';
import TenantSwitcher from '@/components/platform/TenantSwitcher';
import { HelpMenu } from './HelpMenu';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { formatInTz } from '@/lib/companyTime';

export function StrataTopBar() {
  const [pageTitle, setPageTitle] = useState('');
  const [now, setNow] = useState(() => new Date());
  const { user } = useAuth();

  // Poll document.title every 1.5s — React Router doesn't expose a hook
  // for it and each page sets its own title declaratively.
  useEffect(() => {
    const tick = () => setPageTitle(pageTitleFromDocument(document.title));
    tick();
    const id = setInterval(tick, 1500);
    return () => clearInterval(id);
  }, []);

  // Tick the clock once a minute so "Mon, Apr 1" stays accurate.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const dateLabel = formatInTz(now, undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
  });

  return (
    <>
    {user?._impersonating_tenant && (
      <div
        className="hidden lg:flex items-center justify-center gap-2 px-4 py-1.5 print:hidden"
        style={{ background: '#FEF3C7', borderBottom: '1px solid #FDE68A', position: 'sticky', top: 0, zIndex: 21 }}
        data-testid="impersonating-banner"
      >
        <AlertTriangle className="w-3.5 h-3.5 text-[#92400E]" />
        <span className="text-[11px] font-medium text-[#92400E]">
          Viewing as <strong>{user.company_name || user.company_id}</strong> — every action is logged in the platform audit log.
        </span>
      </div>
    )}
    <header
      data-testid="strata-topbar"
      className="hidden lg:flex items-center justify-between gap-6 px-6 py-2.5 print:hidden"
      style={{
        background: user?._impersonating_tenant ? '#FEF7E5' : '#FFFFFF',
        borderBottom: '1px solid var(--strata-border)',
        position: 'sticky',
        top: user?._impersonating_tenant ? 30 : 0,
        zIndex: 20,
      }}
    >
      <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
        <img src="/brand/primary_logo.svg" alt="Strata x Framework" style={{ height: 60 }} />
        {pageTitle && (
          <>
            <span style={{ color: 'var(--strata-border)' }}>·</span>
            <span
              className="text-[11px] font-medium tracking-wide truncate max-w-[240px]"
              style={{ color: 'var(--strata-text-secondary)', fontFamily: 'var(--strata-font-body)' }}
            >
              {pageTitle}
            </span>
          </>
        )}
      </div>

      {/* Smart search rolls out per company (smart_search_enabled); the old search stays until it's on for everyone. */}
      {user?.smart_search_enabled ? <SmartSearch /> : <HeaderSearch />}

      <div className="flex items-center gap-4 flex-shrink-0">
        <TenantSwitcher />
        <NotificationBell />
        <HelpMenu />
        <span
          className="text-[10px] uppercase tracking-[0.2em] font-semibold"
          style={{ color: 'var(--strata-text-muted)', fontFamily: 'var(--strata-font-body)' }}
        >
          {dateLabel}
        </span>
      </div>
    </header>
    </>
  );
}
